import {createCoreRoute, createHiddenRoutes} from '@/stores/apolloPlatform/cores/portalNavRoute';
import {
  TreeNav,
  TreeNodeCore,
  TreeNodePage,
  makeNavigation,
} from '@/stores/apolloPlatform/cores/portalNavigation';

export const useCoresStore = defineStore('cores', {
  state: () => ({
    navTree: undefined as TreeNav | undefined,
    removeNavHook: undefined as (() => void) | undefined,
  }),
  actions: {
    async setNavTree(pages: PaginatedSpherePage) {
      const {tree: navTree, hiddenNodes} = makeNavigation(pages.results);
      const coreRoutes = [
        ...navTree.flatMap((coreNode) => createCoreRoute(coreNode)),
        ...createHiddenRoutes(hiddenNodes), // Linkable, but not in the menu
      ];

      // Replace old core-dynamic with new
      if (this.router.hasRoute('core-dynamic')) this.router.removeRoute('core-dynamic');
      this.router.addRoute({name: 'core-dynamic', path: '', children: coreRoutes});

      this.navTree = reactive(navTree);

      const path = this.router.currentRoute.value.fullPath;
      const destRoute = this.router.resolve(path);

      this.expandNodes(destRoute.meta.node);
      await this.router.replace(destRoute);
      this.registerNavigationCallback(); // For future navigations
    },
    registerNavigationCallback() {
      if (this.removeNavHook) return; // Already registered
      this.removeNavHook = this.router.beforeEach((to) => {
        this.expandNodes(to.meta.node);
      });
    },
    async readPages() {
      try {
        const {data} = await apiGetCmsPages();
        await this.setNavTree(data);
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readPage(detail_url: string, locale?: 'en' | 'nl') {
      try {
        const {data} = await apiGetCmsPage(detail_url, locale);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    expandNodes(startNode: TreeNodeCore | TreeNodePage | undefined) {
      let node = startNode;
      // Index pages are both a page and a folder, so open the folder when landing on the page.
      if (
        node?.children.length &&
        (node.meta.type === 'cms.CoreIndexPage' || node.meta.type === 'cms.CategoryIndexPage')
      )
        reactive(node).expanded = true;
      while (node !== undefined) {
        if (node.node_type !== 'PageNode') break;
        node = node.parent;
        if (node?.children) reactive(node).expanded = true; // Don't collapse non-active nodes
      }
    },
    async switchLanguage() {
      /**
       * This action should be called when the user language has changed.
       * @see {@link stores/apolloPlatform/userSettings/userSettings.ts}
       * Re-fetch using the new Accept-Language header. This is done in the Axios request interceptor.
       * @see {@link api/apolloPlatform/apolloPlatformAxiosInstance.ts}
       */
      await this.readPages();
    },
  },
});
